import {
    ITotalPaxOverrideProvider,
    IPassengersTypesService,
    IPassengerType,
    IPassengerTypeSelector,
    IPassengerTypeSelectorsList,
} from "./passengers-types.service.interface";
import {ServiceBase} from "../service-base";
import {
    ADULT_TYPE_CODES, ANIMAL_TYPE_CODES,
    CHILD_TYPE_CODES, countInfants,
    INFANT_TYPE_CODES,
    PassengerTypeCodesEnum
} from "./passenger-type-codes.enum";
import {
    Adult,
    AdultBlueBenefits, AdultRetired, AdultRetiredWithChild,
    AdultWithChild,
    AdultWithChildAndCompanionForPRM, AdultWithMedicalReason, AdultWithMedicalReasonAndChild,
    AdultWithReducedMobility, Student, StudentWithChild
} from "./models/adult";
import {Child, ChildBlueBenefits} from "./models/child";
import {Infant, InfantOld} from "./models/infant";
import {AdultSelector} from "./selectors/adult-selector";
import {ChildSelector} from "./selectors/child-selector";
import {InfantSelector} from "./selectors/infant-selector";
import {ReadWritePassengersTypesSelectorsList} from "./selectors/read-write-passengers-types-selectors-list";
import {ReadonlyPassengersTypesSelectorsList} from "./selectors/readonly-passengers-types-selectors-list";
import {DogSelector} from "./selectors/dog-selector";
import {Dog} from "./models/dog";

export class PassengersTypesService extends ServiceBase implements IPassengersTypesService {

    createReadWritePassengersTypesSelectorsList(withBlueBenefits: () => boolean,
                                                getPassengersLimitProvider: () => ITotalPaxOverrideProvider,
                                                initialCountValues?: Record<string, number>): IPassengerTypeSelectorsList {
        initialCountValues = initialCountValues ?? {};
        const passengerTypesSelectors = new ReadWritePassengersTypesSelectorsList(this.services, getPassengersLimitProvider, withBlueBenefits);

        const initialAdults = this._matchInitialPassengerTypeCount(initialCountValues, ADULT_TYPE_CODES, 1);
        const initialChildren = this._matchInitialPassengerTypeCount(initialCountValues, CHILD_TYPE_CODES, 0);
        const initialAnimals = this._matchInitialPassengerTypeCount(initialCountValues, ANIMAL_TYPE_CODES, 0);

        const adultSelector = new AdultSelector(this.services, passengerTypesSelectors, initialAdults);
        passengerTypesSelectors.push(adultSelector);
        passengerTypesSelectors.push(new ChildSelector(this.services, passengerTypesSelectors, initialChildren));
        passengerTypesSelectors.push(new InfantSelector(this.services, adultSelector, passengerTypesSelectors,  countInfants(initialCountValues)));
        if(!this.services.configuration.isAgencyPortal) {
            passengerTypesSelectors.push(new DogSelector(this.services, passengerTypesSelectors,  initialAnimals));
        }


        return passengerTypesSelectors;
    }

    createReadonlyPassengersTypesSelectorsList(initialPassengersTypesCount: Record<PassengerTypeCodesEnum, number>): IPassengerTypeSelectorsList {
        return new ReadonlyPassengersTypesSelectorsList(this.services, initialPassengersTypesCount);
    }

    private _matchInitialPassengerTypeCount(initialCountValues: Record<string, number>, passengerTypes: PassengerTypeCodesEnum[], minValue: number): number {
        const matchedCode = passengerTypes.find(c => Boolean(initialCountValues[c]));
        if(matchedCode) {
            return Math.max(minValue, initialCountValues[matchedCode]);
        }

        return minValue;
    }

    createMaturePassengerTypeSelector(passengerTypeCode: PassengerTypeCodesEnum, count: number, passengersTypesSelectorList: IPassengerTypeSelectorsList): IPassengerTypeSelector {
        if(ADULT_TYPE_CODES.includes(passengerTypeCode)) {
            return new AdultSelector(this.services, passengersTypesSelectorList, count);
        } else if(CHILD_TYPE_CODES.includes(passengerTypeCode)) {
            return new ChildSelector(this.services, passengersTypesSelectorList, count);
        }
        else if(ANIMAL_TYPE_CODES.includes(passengerTypeCode)) {
            return new DogSelector(this.services, passengersTypesSelectorList, count)

        } else {
            throw new Error(`Unknown mature passenger type code ${passengerTypeCode}`);
        }
    }

    createInfantTypeSelector(count: number, adultSelector: AdultSelector, passengersTypesSelectorList: IPassengerTypeSelectorsList): IPassengerTypeSelector {
        return new InfantSelector(this.services,
                   adultSelector,
                   passengersTypesSelectorList,
                   count);
    }

    getPassengerType(code: string): IPassengerType {
        switch (code) {
            case PassengerTypeCodesEnum.Adult:
                return new Adult(this.services);
            case PassengerTypeCodesEnum.Child:
                return new Child(this.services);
            case PassengerTypeCodesEnum.AdultWithChild:
                return new AdultWithChild(this.services);
            case PassengerTypeCodesEnum.AdultWithReducedMobility:
                return new AdultWithReducedMobility(this.services);
            case PassengerTypeCodesEnum.Student:
                return new Student(this.services);
            case PassengerTypeCodesEnum.StudentWithChild:
                return new StudentWithChild(this.services);
            case PassengerTypeCodesEnum.AdultWithMedicalReason:
                return new AdultWithMedicalReason(this.services);
            case PassengerTypeCodesEnum.AdultWithMedicalReasonAndChild:
                return new AdultWithMedicalReasonAndChild(this.services);
            case PassengerTypeCodesEnum.AdultRetired:
                return new AdultRetired(this.services);
            case PassengerTypeCodesEnum.AdultRetiredWithChild:
                return new AdultRetiredWithChild(this.services);
            case PassengerTypeCodesEnum.Infant:
                return new Infant(this.services);
            case PassengerTypeCodesEnum.InfantOld:
                return new InfantOld(this.services);
            case PassengerTypeCodesEnum.AdultWithChildAndCompanionForReducedMobility:
                return new AdultWithChildAndCompanionForPRM(this.services);
            case PassengerTypeCodesEnum.AdultClubMember:
                return new AdultBlueBenefits(this.services);
            case PassengerTypeCodesEnum.ChildClubMember:
                return new ChildBlueBenefits(this.services);
            case PassengerTypeCodesEnum.Dog:
                return new Dog(this.services);
            default:
                return new Adult(this.services);
        }
    }



    getPassengerTypeCodeForLowFareSearch(withBlueBenefits: boolean): string {
        if(withBlueBenefits) {
            return PassengerTypeCodesEnum.AdultClubMember;
        } else {
            return PassengerTypeCodesEnum.Adult;
        }
    }
    getAllTypeCodes(): PassengerTypeCodesEnum[] {
        return [
            ...ADULT_TYPE_CODES,
            ...CHILD_TYPE_CODES,
            ...INFANT_TYPE_CODES,
            ...ANIMAL_TYPE_CODES
        ];
    }
    getInfantType(): IPassengerType {
        return this.getPassengerType(PassengerTypeCodesEnum.Infant);
    }

    getInfantOldType(): IPassengerType {
        return this.getPassengerType(PassengerTypeCodesEnum.InfantOld);
    }

    getAdultTypes(): IPassengerType[] {
        return ADULT_TYPE_CODES.map(c => this.getPassengerType(c));
    }

    getChildTypes(): IPassengerType[] {
        return CHILD_TYPE_CODES.map(c => this.getPassengerType(c));
    }

    getAnimalTypes(): IPassengerType[] {
        return ANIMAL_TYPE_CODES.map(c => this.getPassengerType(c));
    }

}





